import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Table,
  Typography,
  Space,
  Button,
  message,
  Modal,
  Alert,
  Input,
} from 'antd';
import { getDirectusClient } from '../../lib/directus';
import {
  readUsers,
  readItems,
  createItem,
  updateItem,
  deleteItem,
} from '@directus/sdk';
import Layouts from '../../components/Layouts';
import VendorDetails from './components/VendorDetails';
import moment from 'moment';
import ConsignacionModal from './components/ConsignacionModal';
import { useSelector } from 'react-redux';
import NovedadModal from './components/NovedadModal';
import { formatPrice } from '../../lib/utils';

const { Option } = Select;
const { Title, Text } = Typography;

function Liquidaciones() {
  const [supervisors, setSupervisors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allCajasLiquidadas, setAllCajasLiquidadas] = useState(false);
  const signoMoneda = useSelector(state => state.signoMoneda.signo_moneda);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const user = useSelector(state => state.caja);
  // Add new states after existing useState declarations
  const [hasActiveLiquidacion, setHasActiveLiquidacion] = useState(false);
  const [currentLiquidacionId, setCurrentLiquidacionId] = useState(null);
  const [isConsignacionModalVisible, setIsConsignacionModalVisible] =
    useState(false);

  // Add new state for novedad modal
  const [novedadModalVisible, setNovedadModalVisible] = useState(false);
  const [novedad, setNovedad] = useState('');
  const [diferencia, setDiferencia] = useState(0);

  // Add a new state to store totals
  const [totals, setTotals] = useState({
    total_facturas: 0,
    total_credito: 0,
    total_devoluciones: 0,
  });

  // Add new state at the top with other states
  const [consignaciones, setConsignaciones] = useState([]);

  const fetchSupervisors = async () => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readUsers({
          fields: ['id', 'first_name', 'last_name', 'role.*'],
          filter: {
            role: { name: { _contains: 'supervisor' } },
          },
        })
      );
      setSupervisors(response);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    }
  };

  // Modify fetchVendors to calculate and store totals
  const fetchVendors = async supervisorId => {
    if (!supervisorId) return;
    setLoading(true);
    try {
      const directus = await getDirectusClient();

      // First get all unliquidated cajas
      const cajas = await directus.request(
        readItems('cajas', {
          fields: ['id', 'liquidada', 'vendedor.id', 'vendedor.first_name'],
          filter: {
            _and: [
              { liquidada: { _eq: false } },
              {
                vendedor: {
                  superior: { id: { _eq: supervisorId } },
                  role: { name: { _contains: 'tropas' } },
                },
              },
            ],
          },
        })
      );

      console.log('cajas', cajas);

      // Validar si hay cajas
      if (!cajas.length > 0) {
        setHasActiveLiquidacion(true);
        message.info(
          'No hay cajas pendientes por liquidar para este supervisor'
        );
        setVendors([]);
        setTotals({
          total_facturas: 0,
          total_credito: 0,
          total_devoluciones: 0,
        });
        setLoading(false);
        return;
      }

      const cajasIds = cajas.map(caja => caja.id);
      const facturas = await directus.request(
        readItems('facturas', {
          fields: [
            'id',
            'caja.id',
            'caja.vendedor.id',
            'caja.vendedor.first_name',
            'productos.cantidad',
            'productos.precio_unitario',
            'productos.producto.grupo.impuesto',
            'estado',
            'tipo',
          ],
          filter: {
            caja: { _in: cajasIds },
          },
        })
      );

      // Group by vendedor and calculate totals
      const vendorTotals = cajas.reduce((acc, caja) => {
        const vendorId = caja.vendedor?.id;
        if (!vendorId) return acc;

        if (!acc[vendorId]) {
          acc[vendorId] = {
            id: vendorId,
            first_name: caja.vendedor?.first_name,
            serialesVendidos: 0,
            totalFacturas: 0,
            cajas: [], // Inicializamos el array de cajas
          };
        }

        acc[vendorId].cajas.push(caja.id); // Agregamos el ID de la caja

        // Find facturas for this caja
        const cajaFacturas = facturas.filter(f => f.caja?.id === caja.id);

        cajaFacturas.forEach(factura => {
          if (factura.estado === 'creada' && factura.tipo === 1) {
            // Calculate seriales
            const seriales = (factura.productos || []).reduce(
              (sum, prod) => sum + (prod.cantidad || 0),
              0
            );
            acc[vendorId].serialesVendidos += seriales;

            // Calculate total facturas with tax
            const total = (factura.productos || []).reduce((sum, prod) => {
              const subtotal =
                (prod.cantidad || 0) * (prod.precio_unitario || 0);
              const impuesto = prod.producto?.grupo?.impuesto || 0;
              const impuestoValue = subtotal * (impuesto / 100);
              return sum + (subtotal + impuestoValue);
            }, 0);
            acc[vendorId].totalFacturas += total;
          }
        });

        return acc;
      }, {});

      // Calculate global totals
      const calculatedTotals = facturas.reduce(
        (acc, factura) => {
          const total = (factura.productos || []).reduce((sum, prod) => {
            const subtotal = (prod.cantidad || 0) * (prod.precio_unitario || 0);
            const impuesto = prod.producto?.grupo?.impuesto || 0;
            const impuestoValue = subtotal * (impuesto / 100);
            return sum + (subtotal + impuestoValue);
          }, 0);

          if (factura.tipo === 1) {
            if (factura.estado === 'creada') {
              acc.total_facturas += total;
            } else if (factura.estado === 'devolucion') {
              acc.total_devoluciones += total;
            }
          } else if (factura.tipo === 2) {
            acc.total_credito += total;
          }
          return acc;
        },
        { total_facturas: 0, total_credito: 0, total_devoluciones: 0 }
      );

      setTotals(calculatedTotals);
      setVendors(Object.values(vendorTotals));
    } catch (error) {
      console.error('Error fetching vendors:', error);
      message.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Add new function to check for active liquidacion
  const checkActiveLiquidacion = async supervisorId => {
    if (!supervisorId) return;
    setLoading(true);

    try {
      const directus = await getDirectusClient();

      // Step 1: Search for an incomplete liquidation for the supervisor
      const liquidaciones = await directus.request(
        readItems('liquidacion', {
          fields: ['id', 'estado'],
          filter: {
            _and: [
              { responsable: { _eq: supervisorId } },
              { estado: { _eq: 'incompleta' } },
            ],
          },
          limit: 1,
        })
      );

      const activeLiquidacion = liquidaciones[0];

      if (activeLiquidacion) {
        // Step 2: If a liquidation exists
        setHasActiveLiquidacion(true);
        setCurrentLiquidacionId(activeLiquidacion.id);
        await fetchVendors(supervisorId);
      } else {
        // Step 3: If no liquidation exists
        setHasActiveLiquidacion(false);
        setCurrentLiquidacionId(null);
        setConsignaciones([]);
        await fetchVendors(supervisorId);
      }
    } catch (error) {
      console.error('Error checking active liquidacion:', error);
      message.error('Error al verificar el estado de la liquidación');
      setHasActiveLiquidacion(false);
      setCurrentLiquidacionId(null);
      setVendors([]);
      setTotals({ total_facturas: 0, total_credito: 0, total_devoluciones: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Add function to create liquidacion
  const handleCreateLiquidacion = async () => {
    try {
      const directus = await getDirectusClient();
      const today = moment().startOf('day');
      const tomorrow = moment().endOf('day');

      // Get all cajas for vendors under this supervisor
      const cajas = await directus
        .request(
          readItems('cajas', {
            fields: [
              'id',
              'total_ventas',
              'facturas.tipo',
              'facturas.estado',
              'facturas.productos.cantidad',
              'facturas.productos.precio_unitario',
              'facturas.productos.producto.grupo.impuesto',
            ],
            filter: {
              _and: [
                { liquidada: { _eq: false } },
                { vendedor: { superior: { id: { _eq: selectedSupervisor } } } },
              ],
            },
          })
        )
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.error('Error fetching cajas:', error);
        })
        .finally(async response => {
          console.log('Cajas fetched');
          if (!response || response.length === 0) {
            message.info(
              'No hay cajas pendientes por liquidar para este supervisor'
            );
            return;
          }

          // Get total of consignaciones for these cajas
          const consignaciones = await directus.request(
            readItems('consignaciones', {
              fields: ['monto'],
              filter: {
                liquidacion: { id: { _eq: currentLiquidacionId } },
              },
            })
          );

          const totalConsignaciones = consignaciones.reduce(
            (sum, cons) => sum + parseFloat(cons.monto || 0),
            0
          );

          // Calculate totals from facturas
          const totals = response.reduce(
            (acc, caja) => {
              const facturas = caja.facturas || [];

              facturas.forEach(factura => {
                const total = (factura.productos || []).reduce((sum, prod) => {
                  const subtotal =
                    (prod.cantidad || 0) * (prod.precio_unitario || 0);
                  const impuesto = prod.producto?.grupo?.impuesto || 0;
                  const impuestoValue = subtotal * (impuesto / 100);
                  return sum + (subtotal + impuestoValue);
                }, 0);

                if (factura.tipo === 1) {
                  if (factura.estado === 'creada') {
                    acc.total_facturas += total;
                  } else if (factura.estado === 'devolucion') {
                    acc.total_devoluciones += total;
                  }
                } else if (factura.tipo === 2) {
                  acc.total_credito += total;
                }
              });

              return acc;
            },
            { total_facturas: 0, total_credito: 0, total_devoluciones: 0 }
          );
          const diferencia = totalConsignaciones - totals.total_facturas;
          const requiereNovedad = Math.abs(diferencia) > 0;

          // Create liquidacion with totals
          const liquidacion = await directus.request(
            createItem('liquidacion', {
              ...totals,
              responsable: selectedSupervisor,
              estado: 'incompleta',
              tipo: 'tropas',
              cajas: {
                create: cajas.map(caja => ({
                  liquidacion_id: '+',
                  cajas_id: {
                    id: caja.id,
                  },
                })),
                update: [],
                delete: [],
              },
              diferencia: diferencia,
            })
          );

          setCurrentLiquidacionId(liquidacion.id);
          setHasActiveLiquidacion(true);
          setAllCajasLiquidadas(false); // Reset this state since we just created a new liquidacion

          // Refresh the vendors data to show the new liquidacion
          await fetchVendors(selectedSupervisor);

          // Fetch consignaciones for the new liquidacion
          await fetchConsignaciones(liquidacion.id);

          if (requiereNovedad) {
            message.warning(
              'Se detectó una diferencia. Por favor registre una novedad.'
            );
          } else {
            message.success('Liquidación creada exitosamente');
          }
          //
        });

      // Validar si hay cajas

      // Calculate diferencia
    } catch (error) {
      console.error('Error creating liquidacion:', error);
      message.error('Error al crear la liquidación');
    }
  };

  const handleFinalizeLiquidacion = async () => {
    if (!currentLiquidacionId) return;

    try {
      const directus = await getDirectusClient();
      const today = moment().startOf('day');
      const tomorrow = moment().endOf('day');

      // Get all cajas linked to this liquidacion
      const liquidacion = await directus.request(
        readItems('liquidacion', {
          fields: ['id', 'total_facturas', 'cajas.*'],
          filter: { id: { _eq: currentLiquidacionId } },
        })
      );

      if (!liquidacion?.length) {
        throw new Error('Liquidación no encontrada');
      }

      // Get consignaciones for these cajas
      const consignaciones = await directus.request(
        readItems('consignaciones', {
          fields: ['monto'],
          filter: {
            _and: [
              {
                liquidacion: { id: { _eq: currentLiquidacionId } },
              },
            ],
          },
        })
      );

      const totalConsignaciones = consignaciones.reduce(
        (sum, cons) => sum + parseFloat(cons.monto || 0),
        0
      );

      // Validar si hay consignaciones
      if (totalConsignaciones === 0) {
        message.error('No hay consignaciones registradas');
        return;
      }

      // Calculate diferencia
      const calculatedDiferencia = totalConsignaciones - totals.total_facturas;

      if (Math.abs(calculatedDiferencia) > 0) {
        setDiferencia(calculatedDiferencia);
        setNovedadModalVisible(true);
        return;
      }

      await finalizeLiquidacion(calculatedDiferencia);
    } catch (error) {
      console.error('Error finalizing liquidacion:', error);
      message.error('Error al finalizar la liquidación');
    }
  };

  // Add function to actually finalize liquidacion
  const finalizeLiquidacion = async (
    calculatedDiferencia,
    novedadText = null
  ) => {
    try {
      const directus = await getDirectusClient();

      // First get the cajas IDs from the liquidacion
      const liquidacion = await directus.request(
        readItems('liquidacion', {
          fields: ['id', 'cajas.*'],
          filter: { id: { _eq: currentLiquidacionId } },
        })
      );

      if (!liquidacion?.length) {
        throw new Error('Liquidación no encontrada');
      }

      // Update liquidacion
      await directus.request(
        updateItem('liquidacion', currentLiquidacionId, {
          estado: 'creada',
          diferencia: calculatedDiferencia,
          novedades: novedadText,
          total_facturas: totals.total_facturas,
          total_credito: totals.total_credito,
          total_devoluciones: totals.total_devoluciones,
        })
      );

      // Update all cajas to liquidada = true
      const cajasUpdates = liquidacion[0].cajas.map(cajaObj =>
        directus.request(
          updateItem('cajas', cajaObj.cajas_id, {
            liquidada: true,
          })
        )
      );

      await Promise.all(cajasUpdates);

      setHasActiveLiquidacion(false);
      setCurrentLiquidacionId(null);
      setNovedadModalVisible(false);
      setNovedad('');
      setAllCajasLiquidadas(true); // Set this to true since all cajas are now liquidated
      message.success('Liquidación finalizada exitosamente');

      // Refresh vendors list to reflect updated cajas
      await fetchVendors(selectedSupervisor);
    } catch (error) {
      console.error('Error updating liquidacion:', error);
      message.error('Error al actualizar la liquidación');
    }
  };

  // Add function to fetch consignaciones
  const fetchConsignaciones = async liquidacionId => {
    if (!liquidacionId) return;

    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems('consignaciones', {
          fields: [
            'id',
            'monto',
            'date_created',
            'user_created.first_name',
            'user_created.last_name',
            'tipo',
            'banco.nombre',
            'referencia',
            'nombre',
          ],
          filter: {
            liquidacion: { id: { _eq: liquidacionId } },
          },
        })
      );
      setConsignaciones(response);
    } catch (error) {
      console.error('Error fetching consignaciones:', error);
      message.error('Error al cargar las consignaciones');
    }
  };

  // Add after fetchConsignaciones function
  const handleDeleteConsignacion = async consignacionId => {
    try {
      const directus = await getDirectusClient();
      await directus.request(deleteItem('consignaciones', consignacionId));

      message.success('Consignación eliminada correctamente');
      // Refresh consignaciones list
      fetchConsignaciones(currentLiquidacionId);
      // Refresh vendors to update totals
      fetchVendors(selectedSupervisor);
    } catch (error) {
      console.error('Error deleting consignacion:', error);
      message.error('Error al eliminar la consignación');
    }
  };

  useEffect(() => {
    fetchSupervisors();
  }, []);

  // Update useEffect for selectedSupervisor
  useEffect(() => {
    if (selectedSupervisor) {
      checkActiveLiquidacion(selectedSupervisor);
    } else {
      setVendors([]);
      setHasActiveLiquidacion(false);
      setCurrentLiquidacionId(null);
      setAllCajasLiquidadas(false);
      setTotals({
        total_facturas: 0,
        total_credito: 0,
        total_devoluciones: 0,
      });
      setConsignaciones([]);
    }
  }, [selectedSupervisor]);

  // Add new useEffect
  useEffect(() => {
    if (currentLiquidacionId) {
      fetchConsignaciones(currentLiquidacionId);
    } else {
      setConsignaciones([]);
    }
  }, [currentLiquidacionId]);

  const columns = [
    {
      title: 'Vendedor',
      dataIndex: 'first_name',
      key: 'first_name',
    },
    {
      title: 'Seriales Vendidos',
      dataIndex: 'serialesVendidos',
      key: 'serialesVendidos',
      render: value => value || 0,
    },
    {
      title: 'Total Facturas',
      dataIndex: 'totalFacturas',
      key: 'totalFacturas',
      render: value => formatPrice(value, signoMoneda) || 0,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => {
            setSelectedVendor(record);
            setIsDetailsVisible(true);
          }}
        >
          Ver Detalles
        </Button>
      ),
    },
  ];

  // Add consignaciones columns definition
  const consignacionesColumns = [
    {
      title: 'Fecha',
      dataIndex: 'date_created',
      key: 'date_created',
      render: date => moment(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
    },
    {
      title: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
      render: banco => banco?.nombre || '-', // Changed from banco[0].nombre to banco.nombre
    },
    {
      title: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: value => formatPrice(value, signoMoneda),
    },
    {
      title: 'Creado por',
      key: 'user_created',
      render: (_, record) =>
        `${record.user_created?.first_name || ''} ${
          record.user_created?.last_name || ''
        }`,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          danger
          onClick={() => {
            Modal.confirm({
              title: '¿Está seguro de eliminar esta consignación?',
              content: 'Esta acción no se puede deshacer',
              okText: 'Sí, eliminar',
              cancelText: 'No, cancelar',
              onOk: () => handleDeleteConsignacion(record.id),
            });
          }}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space direction="horizontal" style={{ marginBottom: 16 }}>
            <Title level={4}>Liquidaciones por Supervisor</Title>
            <Button
              type="primary"
              onClick={handleCreateLiquidacion}
              disabled={
                !selectedSupervisor ||
                hasActiveLiquidacion ||
                allCajasLiquidadas
              }
            >
              Crear Liquidación
            </Button>
            {hasActiveLiquidacion && !allCajasLiquidadas && (
              <>
                <Button
                  onClick={() => setIsConsignacionModalVisible(true)}
                  disabled={!selectedSupervisor || !currentLiquidacionId}
                >
                  Crear Consignación
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={handleFinalizeLiquidacion}
                  disabled={!currentLiquidacionId}
                >
                  Finalizar Liquidación
                </Button>
              </>
            )}
          </Space>

          <Select
            style={{ width: 300 }}
            placeholder="Seleccione un supervisor"
            onChange={setSelectedSupervisor}
            allowClear
          >
            {supervisors.map(supervisor => (
              <Option key={supervisor.id} value={supervisor.id}>
                {`${supervisor.first_name} ${supervisor.last_name}`}
              </Option>
            ))}
          </Select>

          <Table
            columns={columns}
            dataSource={vendors}
            loading={loading}
            rowKey="id"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>Total General</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>
                    {vendors.reduce(
                      (sum, vendor) => sum + (vendor.serialesVendidos || 0),
                      0
                    )}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <strong>
                    {formatPrice(totals.total_facturas, signoMoneda)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
              </Table.Summary.Row>
            )}
          />
          {hasActiveLiquidacion && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>
                Consignaciones
              </Title>
              <Table
                columns={consignacionesColumns}
                dataSource={consignaciones}
                loading={loading}
                rowKey="id"
                pagination={false}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <strong>Total Consignaciones</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <strong>
                        {formatPrice(
                          consignaciones.reduce(
                            (sum, cons) => sum + parseFloat(cons.monto || 0),
                            0
                          ),
                          signoMoneda
                        )}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} />
                  </Table.Summary.Row>
                )}
              />
            </>
          )}
        </Space>
      </Card>
      <VendorDetails
        visible={isDetailsVisible}
        vendor={selectedVendor}
        onClose={() => {
          setIsDetailsVisible(false);
          setSelectedVendor(null);
        }}
        cajasIds={selectedVendor?.cajas}
      />

      <ConsignacionModal
        visible={isConsignacionModalVisible}
        liquidacionId={currentLiquidacionId}
        onClose={() => setIsConsignacionModalVisible(false)}
        onSuccess={() => {
          setIsConsignacionModalVisible(false);
          fetchVendors(selectedSupervisor);
          fetchConsignaciones(currentLiquidacionId);
        }}
      />

      <NovedadModal
        visible={novedadModalVisible}
        onCancel={() => {
          setNovedadModalVisible(false);
        }}
        onConfirm={novedadText => {
          finalizeLiquidacion(diferencia, novedadText);
        }}
        diferencia={diferencia}
      />
    </Layouts>
  );
}

export default Liquidaciones;
